import { useState } from 'react';

export default function EditingForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const notes = data.get('notes') as string;
    if (notes && notes.length > 500) {
      return;
    }

    setStatus('submitting');

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6rfpes4k19g20dvrlbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#F8FAFF]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left side info */}
          <div>
            <span className="inline-block px-4 py-1.5 bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs font-semibold rounded-full mb-4 tracking-widest uppercase">
              Submit Your Project
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-5 leading-tight">
              Ready to Get Your Videos Edited?
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              Fill out the form and share your raw footage via a Google Drive or Dropbox link. We'll review your files and get back to you within a few hours with a plan and timeline.
            </p>

            <div className="space-y-5">
              {[
                { icon: 'ri-google-line', title: 'Google Drive', desc: 'Upload your raw clips to a shared Google Drive folder and paste the link in the form.' },
                { icon: 'ri-dropbox-line', title: 'Dropbox', desc: 'Share a Dropbox folder link — we\'ll download your footage and get started right away.' },
                { icon: 'ri-time-line', title: '48-Hour Turnaround', desc: 'Most projects are delivered within 48 hours. Rush delivery available on request.' },
                { icon: 'ri-shield-check-line', title: 'Your Files Stay Private', desc: 'We never share or repurpose your footage. Your content is yours, always.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1E3A8A]/10 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-lg text-[#1E3A8A]`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">We Got Your Project!</h3>
                <p className="text-gray-600 text-sm">We'll review your footage and reach out within a few hours. Check your inbox at <strong>info@bellportbranding.com</strong>.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 px-6 py-2.5 bg-[#1E3A8A] text-white text-sm font-medium rounded-full hover:bg-[#1E3A8A]/90 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form id="video-editing-inquiry-form">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Video Editing Inquiry</h3>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Jane Smith"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="jane@example.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(631) 000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Platform <span className="text-red-500">*</span></label>
                    <select
                      name="platform"
                      required
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] cursor-pointer"
                    >
                      <option value="">Select platform</option>
                      <option value="Instagram Reels">Instagram Reels</option>
                      <option value="TikTok">TikTok</option>
                      <option value="YouTube Shorts">YouTube Shorts</option>
                      <option value="Facebook">Facebook</option>
                      <option value="Multiple Platforms">Multiple Platforms</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Number of Videos <span className="text-red-500">*</span></label>
                  <select
                    name="video_count"
                    required
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] cursor-pointer"
                  >
                    <option value="">How many videos?</option>
                    <option value="1 video">1 video</option>
                    <option value="2-4 videos">2–4 videos</option>
                    <option value="5-10 videos">5–10 videos</option>
                    <option value="10+ videos (monthly)">10+ videos (monthly)</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Google Drive or Dropbox Link <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    name="file_link"
                    required
                    placeholder="https://drive.google.com/... or https://www.dropbox.com/..."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
                  />
                  <p className="text-xs text-gray-400 mt-1">Make sure sharing is set to "Anyone with the link can view"</p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Services Needed</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Music Layover', 'Voiceover', 'Captions', 'Color Grading', 'Motion Graphics', 'Sound Design'].map((svc) => (
                      <label key={svc} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          name="services"
                          value={svc}
                          className="w-4 h-4 accent-[#1E3A8A] cursor-pointer"
                        />
                        <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">{svc}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Additional Notes
                    <span className="text-gray-400 font-normal ml-1">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us about your brand style, vibe, any reference videos, or special requests..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A] resize-none"
                  ></textarea>
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-sm mb-4">Something went wrong. Please try again or email us directly.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3.5 bg-[#1E3A8A] text-white font-medium rounded-full hover:bg-[#1E3A8A]/90 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? 'Submitting...' : 'Submit My Project'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
